import upperFirst from "lodash-es/upperFirst";
import { useEffect, useRef, useState } from "react";

import { mergeClassNames } from "@bokio/utils/classes";

import type { FC, PropsWithChildren } from "react";

import * as styles from "./counterBadge.scss";

export type CounterBadgeColor = "highlight" | "contrast" | "subdued" | "warning";

export interface CounterBadgeProps {
	color?: CounterBadgeColor;
	size?: "normal" | "small" | "xsmall";
	/**
	 * Add a $color-base border. Mainly used when rendering the counter on top of an icon to distinguish it from the base.
	 *
	 * This can't be wrapped from outside the component because we have a border-radius here.
	 */
	border?: boolean;
	/**
	 * An "updated" zoom in animation will be triggered if {@link updateCounter} is defined and the value changes.
	 */
	updateCounter?: string;
	testId?: string;
}

const CounterBadgeStatic: FC<
	PropsWithChildren<CounterBadgeProps & { isAnimating?: boolean; onAnimationEnd?: () => void }>
> = ({ border, children, color = "highlight", size = "normal", testId, isAnimating, onAnimationEnd }) => {
	return (
		<span
			className={mergeClassNames(
				styles.root,
				styles[`color${upperFirst(color)}`],
				styles[`size${upperFirst(size)}`],
				border && styles.border,
				isAnimating && styles.isAnimating,
			)}
			onAnimationEnd={onAnimationEnd}
			data-testid={testId}
		>
			{children}
		</span>
	);
};

const CounterBadgeAnimated: FC<CounterBadgeProps> = props => {
	const isFirstEffectExecutedRef = useRef(false);
	const [isAnimating, setIsAnimating] = useState(false);

	useEffect(() => {
		if (!isFirstEffectExecutedRef.current) {
			isFirstEffectExecutedRef.current = true;
			return;
		}

		if (props.updateCounter === undefined) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
			setIsAnimating(false);
			return;
		}

		setIsAnimating(true);
	}, [props.updateCounter]);

	return <CounterBadgeStatic {...props} isAnimating={isAnimating} onAnimationEnd={() => setIsAnimating(false)} />;
};

export const CounterBadge: FC<PropsWithChildren<CounterBadgeProps>> = props => {
	return props.updateCounter === undefined ? <CounterBadgeStatic {...props} /> : <CounterBadgeAnimated {...props} />;
};
